/**
 * @module routes.Sessions.AllCoachesPublicSession._components.coachNextSlot
 *
 * The chip on a coach card: the soonest time that coach can be booked, in place of
 * the count of session types they offer. A count told an athlete how much choice
 * there was; picking a coach is a question of when they are free, and the number is
 * still one tap away on the coach's own page.
 *
 * Availability is asked for per coach and per booking option (see
 * `useFacilityAvailability` for why), so "soonest" here means the soonest slot on the
 * coach's default option — the same option their calendar opens on.
 */
import { DateTime } from "luxon";

import { AvailableSlotModel } from "app/models/scheduling/AvailableSlotModel";
import { timePattern } from "app/utils/timePattern";

/** What the card shows while, and after, availability is fetched. */
export type NextSlot = DateTime | null;

/**
 * The earliest slot at or after `now`. The endpoint is asked for a day at a time
 * and returns that whole day, so a day that has already partly passed still comes
 * back with its morning in it — those have to be dropped here rather than trusted.
 */
export const earliestSlotFrom = (
  slots: AvailableSlotModel[],
  now: DateTime,
): NextSlot => {
  const upcoming = (slots || [])
    .map((slot) => slot?.startHour)
    .filter((startHour): startHour is DateTime => Boolean(startHour?.isValid))
    .filter((startHour) => startHour >= now)
    .sort((a, b) => a.toMillis() - b.toMillis());

  return upcoming[0] ?? null;
};

/**
 * The first date in `dates` that is not already behind us, as an ISO date. Days are
 * compared as dates rather than instants: a slot later today is still bookable, so
 * today counts as available until it is over.
 */
export const nextAvailableDate = (
  dates: Iterable<string>,
  now: DateTime,
): string | null => {
  const today = now.toISODate();

  return (
    Array.from(dates || [])
      .filter((date) => Boolean(date) && date >= today)
      .sort()[0] ?? null
  );
};

/**
 * "Today, 2:00 PM" · "Tomorrow, 9:00 AM" · "Tue, 19 Aug, 9:00 AM".
 *
 * Named days for the two an athlete cares about most, and a dated day beyond that.
 * The comparison is on calendar days in the slot's own zone, so a slot at 11pm
 * tonight does not read as "Tomorrow" to someone whose clock has passed midnight in
 * another zone.
 */
export const nextSlotLabel = (
  slot: NextSlot,
  now: DateTime,
  labels: {
    today: (time: string) => string;
    tomorrow: (time: string) => string;
    onDay: (day: string, time: string) => string;
  },
  // CFTB-3431: the reader's clock, not a fixed 12-hour one. Defaults to what
  // `timePattern` answers with no preference, so a caller that has none is unchanged.
  pattern: string = timePattern(),
): string => {
  if (!slot?.isValid) {
    return "";
  }

  const time = slot.toFormat(pattern);
  // Rounded because a "day" across a DST change is 23 or 25 hours, so the difference
  // between two midnights comes out as 0.958 or 1.042 and the `=== 1` below would
  // miss it, reading "Tue, 19 Aug" on the one day of the year that should say
  // "Tomorrow". Both operands are midnights in the same zone, so the true value is
  // always within an hour of a whole number and rounding is exact.
  const days = Math.round(
    slot.startOf("day").diff(now.setZone(slot.zone).startOf("day"), "days").days,
  );

  if (days <= 0) {
    return labels.today(time);
  }
  if (days === 1) {
    return labels.tomorrow(time);
  }
  return labels.onDay(slot.toFormat("ccc, d LLL"), time);
};
